import { Children, Dispatch, FC, PropsWithChildren, ReactNode, SetStateAction, useCallback, useState } from 'react';
import { ProductMetaData, ProductSorting } from '../../../models/Product';
import { LoanFormat, mapLoanFormat, mapLoanFormatToObject, mapLoanFormatToUrl } from '../../../models/LoanFormat';
import {
    invalidateProductsFromCatalogue,
    invalidateProductsFromLanguageLearning,
    invalidateProductsFromLote,
    useLibxProductsFromCatalogueQuery,
    useProductsFromCatalogueQuery,
    useProductsFromLanguageLearningQuery,
    useProductsFromLoteQuery
} from './useProductsFromCatalogue';
import { Link, To } from 'react-router-dom';
import { CatalogueProductTile } from '../../shop/catalogue/CatalogueProductTile';
import { LazyLoadSlider } from '../slider/LazyLoadSlider';
import './ProductGroup.scss';
import LibxchangeProductElement from '../../yourborrowbox/libx/shared/LibxchangeProductElement';
import {
    invalidateProductGroupProducts,
    useLibxProductGroupProductsQuery,
    useShopProductGroupProductsQuery
} from './useProductGroupProducts';
import { ProductSpec } from '../../store/api';
import { IssueProductTile } from '../common/IssueProductTile';
import { LoanFormatIcon } from '../common/GildIcon';
import { useBackIssues } from '../../yourborrowbox/collection/hooks/useBackIssues';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { SingleAddOptions, SingleCopyOrMoveOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { useAddLibxProductToCollection } from '../../yourborrowbox/libx/productdetail/api/useAddLibxProductToCollection';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useParams } from 'react-router';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';
import { getLoanFormat } from '../utils/getLoanFormat';
import { DISPLAY_TYPE_MINIMAL } from '../common/displayType/types';
import {
    LOANFORMAT_ALL_OBJECT,
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../loanFormat/constants';
import { useIsInPartnerContext } from '../utils';

type CatalogueProductGroupProps = {
    readonly title: string;
    readonly productSpec: ProductSpec;
    readonly seeAllUrl: To;
    readonly shopLanguage: string | undefined;
};

type LanguageProductGroupProps = {
    readonly title: string;
    readonly productSpec: ProductSpec;
    readonly seeAllUrl: To;
    readonly shopLanguage: string | undefined;
    readonly language: string;
};

type LoteProductGroupProps = {
    readonly title: string;
    readonly productSpec: ProductSpec;
    readonly seeAllUrl: To;
};

type LanguageLearningProductGroupProps = {
    readonly title: string;
    readonly productSpec: ProductSpec;
    readonly seeAllUrl: To;
    readonly shopLanguage: string | undefined;
    readonly languageLearningLanguage?: string;
};

export const CatalogueProductGroup: FC<CatalogueProductGroupProps> = ({
    title,
    productSpec,
    seeAllUrl,
    shopLanguage
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { loanFormat: loanFormatParamValue } = useParams<'loanFormat'>();
    const { raw: rawAgeCategoryParam, value: ageCategoryParam } = useAgeCategorySearchParam();
    const loanFormat =
        mapLoanFormat(loanFormatParamValue) ?? mapLoanFormat(productSpec.loanFormat) ?? LoanFormat.eBooks;
    const ageCategory = rawAgeCategoryParam ? ageCategoryParam : productSpec.ageCategory;

    const pathname =
        typeof seeAllUrl === 'string'
            ? new URL('https://connect.borrowbox.com' + seeAllUrl).pathname
            : seeAllUrl.pathname ?? '';
    const search =
        typeof seeAllUrl === 'string'
            ? new URL('https://connect.borrowbox.com' + seeAllUrl).search
            : seeAllUrl.search ?? '';

    const { isLoading, data: productData } = useProductsFromCatalogueQuery(loanFormat, productSpec.filters, {
        ageCategory,
        sortBy: productSpec.sortBy as ProductSorting,
        shopLanguage
    });

    const searchParams = new URLSearchParams(search);
    if (ageCategory) {
        searchParams.set('ageCategory', ageCategory);
    }

    if (isMultiLanguageShopEnabled) {
        seeAllUrl = `/shop/${shopLanguage ?? 'all'}/${mapLoanFormatToUrl(
            loanFormat
        )}/products?${searchParams.toString()}`;
    } else if (pathname.startsWith('/catalogue')) {
        seeAllUrl = `/catalogue/${mapLoanFormatToUrl(loanFormat)}/products?${searchParams.toString()}`;
    }

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            title={title}
            seeAllUrl={seeAllUrl}
            shopLanguage={shopLanguage}
            addToCartCallback={() => invalidateProductsFromCatalogue(loanFormat).then()}
        />
    );
};

export const LanguageProductGroup: FC<LanguageProductGroupProps> = ({
    title,
    productSpec,
    seeAllUrl,
    shopLanguage,
    language
}) => {
    const { loanFormat: loanFormatParamValue } = useParams<'loanFormat'>();
    const loanFormatParam = mapLoanFormat(loanFormatParamValue);
    const { raw: rawAgeCategoryParam, value: ageCategoryParam } = useAgeCategorySearchParam();
    const loanFormat = loanFormatParam ?? mapLoanFormat(productSpec.loanFormat) ?? LoanFormat.eBooks;
    const ageCategory = rawAgeCategoryParam ? ageCategoryParam : productSpec.ageCategory;

    const search =
        typeof seeAllUrl === 'string'
            ? new URL('https://connect.borrowbox.com' + seeAllUrl).search
            : seeAllUrl.search ?? '';
    const productFilter =
        // TODO [cleanup] remove this hacky language code detector
        language.length === 3 && language.toLowerCase() === language
            ? {
                  ...(productSpec.filters ?? {}),
                  languageCode: { include: [language] }
              }
            : {
                  ...(productSpec.filters ?? {}),
                  language: { include: [language] }
              };

    const searchParams = new URLSearchParams(search);
    if (ageCategory) {
        searchParams.set('ageCategory', ageCategory);
    }

    seeAllUrl = `/shop/${shopLanguage ?? 'all'}/${mapLoanFormatToUrl(loanFormat)}/languages/${encodeURIComponent(
        language
    )}/products?${searchParams.toString()}`;

    const { isLoading, data: productData } = useProductsFromCatalogueQuery(loanFormat, productFilter, {
        ageCategory,
        sortBy: productSpec.sortBy as ProductSorting,
        shopLanguage
    });

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            title={title}
            seeAllUrl={seeAllUrl}
            shopLanguage={shopLanguage}
            addToCartCallback={() => invalidateProductsFromCatalogue(loanFormat).then()}
        />
    );
};

// BACK ISSUES

type BackIssueProps = {
    readonly product: ProductMetaData;
};

export const BackIssues: FC<BackIssueProps> = ({ product }) => {
    const { t } = useTranslation();

    const { data: backIssues, isLoading } = useBackIssues(product.id);

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={backIssues}
            title={t('components.productDetails.backIssues')}
            shopLanguage={undefined}
        >
            {({ product: issueProduct, setProductTileExpanded }) => (
                <IssueProductTile
                    key={issueProduct.id}
                    product={issueProduct}
                    minimalView={true}
                    hoverListener={setProductTileExpanded}
                />
            )}
        </ProductGroupRenderer>
    );
};

export const LoteProductGroup: FC<LoteProductGroupProps> = ({ title, productSpec, seeAllUrl }) => {
    const { value: ageCategory } = useAgeCategorySearchParam();
    const loanFormat = productSpec.loanFormat ?? LoanFormat.eBooks;

    const { isLoading, data: productData } = useProductsFromLoteQuery(loanFormat, productSpec.filters, {
        ageCategory: productSpec.ageCategory ?? ageCategory,
        sortBy: productSpec.sortBy as ProductSorting,
        shopLanguage: undefined
    });

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            title={title}
            seeAllUrl={seeAllUrl}
            shopLanguage={undefined}
            addToCartCallback={() => invalidateProductsFromLote(loanFormat).then()}
        />
    );
};

export const LanguageLearningProductGroup: FC<LanguageLearningProductGroupProps> = ({
    title,
    productSpec,
    seeAllUrl,
    shopLanguage,
    languageLearningLanguage
}) => {
    const { loanFormat: loanFormatParamValue } = useParams<'loanFormat'>();
    const { raw: rawAgeCategoryParam, value: ageCategoryParam } = useAgeCategorySearchParam();
    const loanFormat =
        mapLoanFormat(loanFormatParamValue) ?? mapLoanFormat(productSpec.loanFormat) ?? LoanFormat.eBooks;
    const ageCategory = rawAgeCategoryParam ? ageCategoryParam : productSpec.ageCategory;

    const { isLoading, data: productData } = useProductsFromLanguageLearningQuery(loanFormat, productSpec.filters, {
        ageCategory,
        sortBy: productSpec.sortBy as ProductSorting,
        shopLanguage,
        language: languageLearningLanguage
    });

    if (languageLearningLanguage) {
        const search =
            typeof seeAllUrl === 'string'
                ? new URL('https://connect.borrowbox.com' + seeAllUrl).search
                : seeAllUrl.search ?? '';

        const searchParams = new URLSearchParams(search);
        if (ageCategory) {
            searchParams.set('ageCategory', ageCategory);
        }

        seeAllUrl = `/shop/${shopLanguage ?? 'all'}/languagelearning/${encodeURIComponent(
            languageLearningLanguage
        )}/products/${mapLoanFormatToUrl(loanFormat)}?${searchParams.toString()}`;
    }

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            title={title}
            seeAllUrl={seeAllUrl}
            shopLanguage={shopLanguage}
            addToCartCallback={() => invalidateProductsFromLanguageLearning(loanFormat).then()}
        />
    );
};

type LibxCatalogueProductGroupProps = {
    readonly title: string;
    readonly productSpec: ProductSpec;
    readonly seeAllUrl: To;
    readonly shopLanguage: string | undefined;
};

export const LibxCatalogueProductGroup: FC<LibxCatalogueProductGroupProps> = ({
    title,
    productSpec,
    seeAllUrl,
    shopLanguage
}) => {
    const { value: ageCategory } = useAgeCategorySearchParam();
    const loanFormat = productSpec.loanFormat ?? LoanFormat.eBooks;

    const { isLoading, data: productData } = useLibxProductsFromCatalogueQuery(loanFormat, productSpec.filters, {
        ageCategory: productSpec.ageCategory ?? ageCategory,
        sortBy: productSpec.sortBy as ProductSorting,
        shopLanguage
    });

    const { mutate: addToCollectionMutation } = useAddLibxProductToCollection();

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            seeAllUrl={seeAllUrl}
            title={title}
            shopLanguage={shopLanguage}
        >
            {({ product, key, setProductTileExpanded, shopLanguage }) => (
                <LibxchangeProductElement
                    key={key}
                    onAddToCollection={addToCollectionMutation}
                    displayType={DISPLAY_TYPE_MINIMAL}
                    product={product}
                    hoverListener={setProductTileExpanded}
                    minimalView={true}
                    shopLanguage={shopLanguage}
                />
            )}
        </ProductGroupRenderer>
    );
};

type ProductGroupProps = {
    readonly title: string;
    readonly productGroupId: string;
    readonly shopLanguage: string | undefined;
};

export const ProductGroup: FC<ProductGroupProps> = ({ title, productGroupId, shopLanguage }) => {
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { loanFormat: loanFormatParamValue } = useParams<'loanFormat'>();
    const loanFormat = getLoanFormat(loanFormatParamValue).requestKey;

    const { isLoading, data: productData } = useShopProductGroupProductsQuery(productGroupId, {
        ageCategory,
        loanFormat,
        shopLanguage
    });

    return (
        <ProductGroupRenderer
            isLoading={isLoading}
            productData={productData}
            title={title}
            shopLanguage={shopLanguage}
            addToCartCallback={() => invalidateProductGroupProducts(productGroupId).then()}
        />
    );
};

type LibxProps = {
    readonly title: string;
    readonly productGroupId: string;
    readonly shopLanguage: string | undefined;
};

export const LibxProductGroup: FC<LibxProps> = ({ title, productGroupId, shopLanguage }) => {
    const { value: ageCategory } = useAgeCategorySearchParam();

    const { isLoading, data: productData } = useLibxProductGroupProductsQuery(productGroupId, {
        ageCategory,
        shopLanguage
    });

    const { mutate: addToCollectionMutation } = useAddLibxProductToCollection(() => {
        invalidateProductGroupProducts(productGroupId).then();
    });

    return (
        <ProductGroupRenderer isLoading={isLoading} productData={productData} title={title} shopLanguage={shopLanguage}>
            {({ product, key, setProductTileExpanded, shopLanguage }) => (
                <LibxchangeProductElement
                    key={key}
                    onAddToCollection={addToCollectionMutation}
                    displayType={DISPLAY_TYPE_MINIMAL}
                    product={product}
                    hoverListener={setProductTileExpanded}
                    minimalView={true}
                    shopLanguage={shopLanguage}
                />
            )}
        </ProductGroupRenderer>
    );
};

type ProductGroupTileProps = {
    product: ProductMetaData;
    key: number;
    changePrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    setProductTileExpanded: Dispatch<SetStateAction<boolean>>;
    shopLanguage: string | undefined;
    isInPartnerContext: boolean;
};

type ProductGroupRendererProps = {
    isLoading: boolean;
    productData: { total?: number; products?: readonly ProductMetaData[] };
    title: string;
    seeAllUrl?: To;
    shopLanguage: string | undefined;
    addToCartCallback?: () => void;
    children?: (props: ProductGroupTileProps) => ReactNode;
};

const ProductGroupRenderer: FC<ProductGroupRendererProps> = ({
    isLoading,
    productData,
    title,
    seeAllUrl,
    shopLanguage,
    addToCartCallback = () => {},
    children = ({ product, key, changePrice, setProductTileExpanded, shopLanguage, isInPartnerContext }) => (
        <CatalogueProductTile
            key={key}
            onAddPrice={changePrice}
            product={product}
            minimalView={true}
            hoverListener={setProductTileExpanded}
            productSelection={undefined}
            shopLanguage={shopLanguage}
            isProductSelectable={false}
            loanFormat={mapLoanFormatToObject(product.loanFormat).key}
            isInPartnerContext={isInPartnerContext}
            onSelectProduct={() => {}}
            onProductImageClick={undefined}
            isProductSelected={false}
        />
    )
}) => {
    const isInPartnerContext = useIsInPartnerContext();
    const [productTileExpanded, setProductTileExpanded] = useState(false);
    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: addToCartCallback });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const products = productData.products ?? [];
    const totalProducts = productData.total ?? 0;

    if (!isLoading && products.length < 1) {
        return null;
    }

    const loanFormatIcon = determineLoanFormat(products);

    return (
        <>
            <ProductGroupContainer
                title={title}
                isLoading={isLoading}
                totalProducts={totalProducts}
                seeAllUrl={seeAllUrl}
                loanFormatIcon={loanFormatIcon}
                isProductTileExpanded={productTileExpanded}
                showMoreOnLastTile={totalProducts > products.length}
            >
                {products
                    .map((product, key) => ({
                        product,
                        key,
                        changePrice,
                        setProductTileExpanded,
                        shopLanguage,
                        isInPartnerContext
                    }))
                    .map(children)}
            </ProductGroupContainer>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};

export const determineLoanFormat = (products: readonly Pick<ProductMetaData, 'loanFormat'>[]): string => {
    if (
        products.every(
            p =>
                p.loanFormat === LoanFormat.eMagazineIssues ||
                p.loanFormat === LoanFormat.eMagazines ||
                p.loanFormat === LoanFormat.ePress ||
                p.loanFormat === LoanFormat.ePressIssues
        )
    ) {
        return LOANFORMAT_EPRESS_OBJECT.icon;
    } else if (products.every(p => p.loanFormat === LoanFormat.eAudiobooks)) {
        return LOANFORMAT_EAUDIOBOOK_OBJECT.icon;
    } else if (products.every(p => p.loanFormat === LoanFormat.eBooks)) {
        return LOANFORMAT_EBOOK_OBJECT.icon;
    } else {
        return LOANFORMAT_ALL_OBJECT.icon;
    }
};

type ProductGroupContainerProps = {
    title: string;
    loanFormatIcon: string;
    isLoading: boolean;
    seeAllUrl?: To;
    showMoreOnLastTile: boolean;
    totalProducts: number;
    isProductTileExpanded: boolean;
    isEAudioBookOnly?: boolean;
};

export const ProductGroupContainer: FC<PropsWithChildren<ProductGroupContainerProps>> = ({
    title,
    isLoading,
    totalProducts,
    seeAllUrl,
    showMoreOnLastTile,
    isProductTileExpanded,
    loanFormatIcon,
    children,
    isEAudioBookOnly = loanFormatIcon === LOANFORMAT_EAUDIOBOOK_OBJECT.icon
}) => {
    const containerClassName = isEAudioBookOnly ? 'pg-eaudiobooks' : 'pg-ebooks';
    const { t } = useTranslation();
    const numberOfChildren = Children.count(children);

    return (
        <div
            className={classNames(
                { 'min-h-screen': isLoading },
                `product-group new-product-group ${containerClassName}`
            )}
            data-displayname={'CatalogueProductGroup'}
        >
            <div className="product-group-header">
                <h2>{title}</h2>
                {seeAllUrl ? <SeeAllLink url={seeAllUrl} /> : null}
                <div className="new-product-group__spacer" />
                <span className="marked smoky smaller display-inline-flex">
                    <LoanFormatIcon icon={loanFormatIcon} />
                    <span>{t('numberFormat.numberWithoutDecimalPoint', { val: totalProducts })}</span>
                </span>
            </div>
            {isLoading ? (
                <span>{t('components.filterBar.loading')}</span>
            ) : (
                <LazyLoadSlider
                    className={`new-product-group__slider ${isProductTileExpanded ? 'expanded' : ''}`}
                    itemWidth={195}
                    itemHeight={isEAudioBookOnly ? 280 : 400}
                    initialOffset={0}
                    initialItemsToShow={1}
                >
                    {showMoreOnLastTile && seeAllUrl
                        ? Children.map(children, (child, i) => {
                              const isLast = i === numberOfChildren - 1;

                              if (isLast) {
                                  return (
                                      <SeeMoreLink key={i} url={seeAllUrl}>
                                          {child}
                                      </SeeMoreLink>
                                  );
                              }

                              return child;
                          })
                        : children}
                </LazyLoadSlider>
            )}
        </div>
    );
};

type SeeAllLinkProps = {
    readonly url: To;
};

export const SeeAllLink: FC<SeeAllLinkProps> = ({ url }) => {
    const { t } = useTranslation();

    return (
        <Link to={url} className="material-link" style={{ display: 'inline-flex' }}>
            <span>{t('seeAll')}</span>
            <i className="material-icons">keyboard_arrow_right</i>
        </Link>
    );
};

type SeeMoreLinkProps = {
    readonly url: To;
};

const SeeMoreLink: FC<PropsWithChildren<SeeMoreLinkProps>> = ({ url, children }) => {
    const KeyboardArrowRight = () => <i className="material-icons">keyboard_arrow_right</i>;

    return (
        <div style={{ float: 'left', width: '170px' }} key={'see_more_tile'}>
            {children}
            <Link to={url} className="see-more">
                <span>
                    <Trans
                        i18nKey="components.productDetails.seeMore"
                        components={{
                            1: <KeyboardArrowRight />
                        }}
                    />
                </span>
            </Link>
        </div>
    );
};
